<script setup>
import {debounce} from 'lodash-es';
import CheckoutBlock from "~/components/templates/CheckoutBlock.vue";
import Form from "~/components/atoms/Form.vue";
import InputsGroup from "~/components/atoms/Inputs/InputsGroup.vue";
import FormInput from "~/components/atoms/FormInput.vue";
import HorizontalMenu from "~/components/molecules/HorizontalMenu.vue";
import {useStartupStore} from "~/store/sturtupStore.js";
import {useCartStore} from "~/store/cartStore";
import {useUserStore} from "~/store/userStore";
import KeyboardIcon from "~/components/icons/KeyboardIcon.vue";
import CheckIcon from "~/components/icons/CheckIcon.vue";
import Text from "~/components/atoms/Text.vue";

const {t} = useI18n();

const startupStore = useStartupStore();
const store = useCartStore();
const userStore = useUserStore();
const componentTag = (device) => device.isMobile ? 'div' : InputsGroup;
const emits = defineEmits(['form']);
const config = useRuntimeConfig()

const fillAdmin = () => {
  store.saveEmail('tailer12012@gmail.com')
  store.saveClientData('phone', '+380637463289');
  store.saveClientData('first_name', 'Tailer');
  store.saveClientData('second_name', 'Ambrose');
  store.saveClientData('address', 'Krongårdsringen 10');
  store.saveClientData('zip', '97434');
}


const companyNumberMin = computed(() => config.public.locale === 'da' ? 8 : 10)

const localSites = {
  SE: 'https://hillceramic.se/',
  DK: 'https://hillceramic.dk/',
  NO: 'https://hillceramic.no/',
}

const localeCountry = {
  sv: 'SE',
  da: 'DK',
  nb: 'NO',
  no: 'NO',
}

const localSiteNotice = computed(() => {
  const countryId = store.content?.client_data?.country
  const country = startupStore.countries.find((item) => item.id == countryId)
  const code = country?.code?.toUpperCase()
  const currentCode = localeCountry[config.public.locale]

  if (!code || !currentCode || code === currentCode || !localSites[code]) {
    return null
  }

  return {
    url: localSites[code],
    text: t('checkout.local_site.message', {
      country: t(`checkout.local_site.countries.${code}`),
      local: t(`checkout.local_site.locals.${code}`),
    }),
  }
})

const companyNumberValidations = computed(() => [
  'required',
  `min:${companyNumberMin.value}`,
])

const debouncedFields = {
  phone: debounce((e) => store.saveClientData('phone', e.target.value), 500),
  company_name: debounce((e) => store.saveClientData('company_name', e.target.value), 500),
  company_number: debounce((e) => store.saveClientData('company_number', e.target.value), 500),
  first_name: debounce((e) => store.saveClientData('first_name', e.target.value), 500),
  second_name: debounce((e) => store.saveClientData('second_name', e.target.value), 500),
  address: debounce((e) => store.saveClientData('address', e.target.value), 500),
  zip: debounce((e) => store.saveClientData('zip', e.target.value), 500),
}

const debouncedSaveEmail = debounce((e) => {
  store.saveEmail(e.target.value)
}, 500)

const flushDebouncedFields = async () => {
  const pending = [
    ...Object.values(debouncedFields).map((fn) => fn.flush?.()),
    debouncedSaveEmail.flush?.(),
  ].filter((result) => result != null && typeof result.then === 'function')

  // Wait for flushed save requests so sync does not race with last keystrokes.
  if (pending.length) {
    await Promise.all(pending)
  }
}

defineExpose({
  flushDebouncedFields,
})
</script>

<template>
  <CheckoutBlock @rightClick="fillAdmin" :title="t('checkout.information')"
                 class="form"
                 :right-button="userStore.isAdmin ? {title:'Fill', icon:KeyboardIcon} : null"
                 :number="1">
    <div class="g-32" v-if="store.initialized">
      <div class="row full-width type menu">
        <HorizontalMenu :hide-on-mobile="false" :items="[
            {title:t('checkout.form.private'),callback:()=>{store.content.client_data.type='person'; store.saveClientData('type',store.content.client_data.type)}, active:store.content?.client_data?.type === 'person'},
            {title: t('checkout.form.company'), callback:()=>{store.content.client_data.type='organization'; store.saveClientData('type',store.content.client_data.type)}, active:store.content?.client_data?.type === 'organization'}]"/>
      </div>

      <Form id="checkout-form" @submit="$emit('submit')">
        <template #default>
          <component :is="componentTag($device)" class="g-16 the-same-gap">
            <FormInput :with-validation-approve="true" :validations="['required','email']"
                       @input="debouncedSaveEmail" v-model="store.content.email"
                       :label="$t('checkout.form.email')"/>
            <FormInput :with-validation-approve="true" :validations="['required','min:10']"
                       v-model="store.content.client_data.phone"
                       @input="debouncedFields.phone"
                       :label="$t('checkout.form.phone')"/>
          </component>
          <component :is="componentTag($device)" class="g-16 the-same-gap"
                     v-if="store.content?.client_data?.type === 'organization'">
            <FormInput :with-validation-approve="true" :validations="['required']"
                       v-model="store.content.client_data.company_name"
                       @input="debouncedFields.company_name"
                       :label="$t('checkout.form.company_name')"/>
            <FormInput
                :with-validation-approve="true"
                :validations="companyNumberValidations"
                @input="debouncedFields.company_number"
                v-model="store.content.client_data.company_number"
                :label="$t('checkout.form.company_number')"
            />
          </component>
          <component :is="componentTag($device)" class="g-16 the-same-gap">
            <FormInput :with-validation-approve="true" :validations="['required','min:2']"
                       v-model="store.content.client_data.first_name"
                       @input="debouncedFields.first_name"
                       :label="$t('checkout.form.first_name')"/>
            <FormInput :with-validation-approve="true" :validations="['required','min:2']"
                       v-model="store.content.client_data.second_name"
                       @input="debouncedFields.second_name"
                       :label="$t('checkout.form.second_name')"/>
          </component>
          <component :is="componentTag($device)" class="g-16 the-same-gap">
            <FormInput :with-validation-approve="true" :validations="['required','address_not_valid']"
                       v-model="store.content.client_data.address"
                       @input="debouncedFields.address"
                       :label="$t('checkout.form.address')"/>
            <InputsGroup>
              <div>
                <FormInput :with-validation-approve="true"
                           :validations="['required',store.content.client_data.country == 1 ? 'min:5' : 'min:4']"
                           v-model="store.content.client_data.zip"
                           @input="debouncedFields.zip"
                           :label="$t('checkout.form.zip')"/>

                <div style="position: absolute; bottom: -25px;" v-if="store.zipIsValid && store.content.client_data.city" class="row">
                  <CheckIcon width="20px" height="15px" stroke="#45b370"/>
                  {{ store.content.client_data.city }}
                </div>
              </div>
              <div>
                <FormInput type="select" :label="$t('form.fields.country')"
                           @change="store.saveClientData('country',$event)"
                           v-if="store.content?.client_data?.country"
                           name="client_data[country]"
                           v-model="store.content.client_data.country"
                           :options="startupStore.countries.map(country => ({
                        key: country.id,
                        value: country.name,
                     }))"/>

                <div v-if="localSiteNotice" style="margin-top: 10px;">
                  <Text tag="b" style="font-size: 13px; color: #282828;">
                    {{ localSiteNotice.text }}
                    <a style="color:blue;" :href="localSiteNotice.url" target="_blank" rel="noopener">{{ localSiteNotice.url }}</a>
                  </Text>
                </div>
              </div>
            </InputsGroup>
          </component>
        </template>

        <template #submit></template>
      </Form>
    </div>
  </CheckoutBlock>
</template>

<style scoped lang="scss">
.form:deep(.block-content) {
  @include smd {
    padding-bottom: 36px;
  }
}

.form:deep(button) {
  height: 0 !important;
  background: transparent !important;
}

.type:deep(.horizontal-menu-item) {
  cursor: pointer;
  font-size: 14px;
  color: $disabled-text;

  &.active {
    color: $black;
  }
}

.menu:deep(a) {
  @include smd {
    font-size: 12px;
  }
}
</style>
